<template>
    <div>
        <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
                <v-btn-icon
                    v-bind="props"
                    icon="$oss-help"
                    @click="activateHelp = !activateHelp"
                    :style="topButtonStyle"
                >
                </v-btn-icon>
            </template>
            <span>Tutoriels</span>
        </v-tooltip>
        <oss-dialog
            @keydown.esc="activateHelp = false"
            @previous="previousStep()"
            :previous-display="['editionTutorials', 'displayTutorial'].includes(currentStep)"
            title="Tutoriels"
            v-model="activateHelp"
            v-if="!isAuth() && activateHelp"
            min-height="400px"
            min-width="500px"
        >
            <div class="tutorials-container">
                <tutorials-list
                    ref="tutorialWizard"
                    Index
                    :starterComponents="starterComponents"
                    :tutorials="tutorials"
                    @refresh="refresh()"
                />
            </div>
        </oss-dialog>
    </div>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import TUTORIALS_BY_IDS from "../../graphql/tutorials/TutorialsByIds.gql";
import TUTORIALS from "../../graphql/tutorials/Tutorials.gql";
import { User } from "../../store/store";
import TutorialsListComp, { TutorialsList } from "./TutorialsList.vue"; // Assurez-vous que le chemin est correct
import getEnv from "../../env";
import { Component, Vue, toNative, Prop, Watch, Ref } from "vue-facing-decorator";

const env = getEnv();

@Component({
    components: {
        TutorialsList: TutorialsListComp,
    },
})
export class TutorialButtonDialog extends Vue {
    @Prop({ default: null }) initialPath: string | null;
    @Prop({ default: null }) starterComponents: string | null;
    @Ref("tutorialWizard") tutorialWizard!: TutorialsList;

    localEnv = env.environnement;

    activateHelp = false;
    alreadyAskedEmailVerification = false;

    get currentUser(): User {
        return this.$store.state.user;
    }

    get topButtonStyle() {
        let style = "pointer-events:auto; margin-right: 3px;";
        if (!this.$vuetify.display.mobile) {
            style += " color: #000;";
        }
        return style;
    }

    get isFirstTimeUser() {
        if (this.tutorials.length > 0 && this.starterComponents) {
            let starterComponent = this.starterComponents;

            if (starterComponent.includes(";")) {
                starterComponent = starterComponent.replace(";", "");
            }
            // Filtrer les tutoriels qui ont le starterComponents actuel dans leur liste de starterComponents
            const relevantTutorials = this.tutorials.filter((tutorial) => {
                return (
                    tutorial.starterComponents &&
                    tutorial.starterComponents.split(";").includes(starterComponent)
                );
            });

            // Vérifier si l'utilisateur actuel n'a aucune activité enregistrée pour ces tutoriels filtrés
            return relevantTutorials.every((tutorial) => {
                return !tutorial.UserActivity.some(
                    (activity) => activity.userId === this.currentUser.id,
                );
            });
        }
        return false;
    }

    get storedTutorialIds() {
        if (this.$store.state.componentDictionary) {
            const dictionary = this.$store.state.componentDictionary;
            let starterComponents = null;

            if (this.starterComponents) {
                starterComponents = this.starterComponents
                    .split(";")
                    .map((component) => component.trim());
            }

            let storedTutorialIds = [];

            if (starterComponents && starterComponents.length) {
                starterComponents.forEach((component) => {
                    if (dictionary[component]) {
                        storedTutorialIds = storedTutorialIds.concat(
                            dictionary[component].split(", ").map((id) => id.trim()),
                        );
                    }
                });
            }

            // if (storedTutorialIds.length === 0 && dictionary["Sans starterComponents"]) {
            //     storedTutorialIds = dictionary["Sans starterComponents"].split(", ").map(id => id.trim());
            // }

            return storedTutorialIds || [];
        }
        return [];
    }

    get currentStep() {
        return this.tutorialWizard?.step || "";
    }

    @Apollo({
        query() {
            if (
                this.starterComponents &&
                this.starterComponents !== "Sans starterComponents" &&
                this.storedTutorialIds?.length
            ) {
                return TUTORIALS_BY_IDS;
            }
            return TUTORIALS;
        },
        variables() {
            if (this.starterComponents && this.storedTutorialIds?.length) {
                return {
                    ids: this.storedTutorialIds,
                };
            }
            return {
                search: this.search,
            };
        },
        fetchPolicy: "network-only",
        debounce: 500,
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.tutorialsByIds?.tutorials) {
                    this.tutorials = data.tutorialsByIds.tutorials;
                } else {
                    if (data?.tutorials?.tutorials) {
                        this.tutorials = data.tutorials.tutorials;
                    } else {
                        this.tutorials = [];
                    }
                }
            }
        },
    })
    tutorials: any = [];

    previousStep() {
        this.tutorialWizard.goBack();
    }

    isAuth() {
        const name = this.$route.name;
        if (
            name === "login" ||
            name === "signup" ||
            name === "password-recovery" ||
            name === "password-reset" ||
            name === "email-verification" ||
            name === "maintenance"
        ) {
            return true;
        }
        return false;
    }

    refresh() {
        this.$apollo.queries.tutorials.refetch();
        this.$apollo.queries.tutorials.refetch();
    }
}

export default toNative(TutorialButtonDialog);
</script>

<style scoped>
.relative {
    position: relative;
}

.ping-container {
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    bottom: -0.5rem;
    left: -0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.ping-animate {
    animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
    position: absolute;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background-color: #f9b12a;
    opacity: 0.95;
    pointer-events: none;
}

@keyframes ping {
    75%,
    100% {
        transform: scale(2);
        opacity: 0;
    }
}

.tutorials-container {
    height: calc(100vh - 80px);
    max-height: 100%;
    overflow: auto;
}
</style>
